import dayjs from 'dayjs';
import { z as zod } from 'zod';

// ----------------------------------------------------------------------

type InputProps = {
  message?: {
    required_error?: string;
    invalid_type_error?: string;
  };
  minFiles?: number;
  required?: boolean;
  isValidPhoneNumber?: (text: string) => boolean;
};

export const schemaHelper = {
  /**
   * Phone number
   * defaultValue === ''
   */
  phoneNumber: (props?: InputProps) =>
    zod
      .string({
        required_error: props?.message?.required_error ?? 'Phone number is required!',
        invalid_type_error: props?.message?.invalid_type_error ?? 'Invalid phone number!',
      })
      .min(1, {
        message: props?.message?.required_error ?? 'Phone number is required!',
      })
      .refine(
        (data) => {
          const isValid = props?.isValidPhoneNumber || ((val: string) => /^[0-9]{10}$/.test(val));
          return isValid(data);
        },
        {
          message: props?.message?.invalid_type_error ?? 'Invalid phone number!',
        }
      ),

  /**
   * name
   * defaultValue === ''
   */
  name: (props?: InputProps) =>
    zod.string().min(1, { message: props?.message?.required_error ?? 'name is required!' }),
  /**
   * email
   * defaultValue === ''
   */
  email: (props?: InputProps) =>
    zod
      .string()
      .min(1, { message: props?.message?.required_error ?? 'Email is required!' })
      .email({
        message: props?.message?.invalid_type_error ?? 'Email must be a valid email address!',
      }),
  /**
   * password
   * defaultValue === ''
   */
  password: (props?: InputProps) =>
    zod
      .string()
      .min(1, { message: 'Password is required!' })
      .min(6, { message: 'Password must be at least 6 characters!' }),
  /**
   * referral
   * defaultValue === ''
   */
  referral: (props?: InputProps) =>
    zod.string().min(1, { message: props?.message?.required_error ?? 'this is required!' }),
  /**
   * text
   * defaultValue === ''
   */
  text: (props?: InputProps) =>
    zod.string().min(1, { message: props?.message?.required_error ?? 'this is required!' }),
  /**
   * Date
   * defaultValue === null
   */
  date: (props?: InputProps) =>
    zod.coerce
      .date()
      .nullable()
      .transform((dateString, ctx) => {
        const date = dayjs(dateString).format();

        const stringToDate = zod.string().pipe(zod.coerce.date());

        if (!dateString) {
          ctx.addIssue({
            code: zod.ZodIssueCode.custom,
            message: props?.message?.required_error ?? 'Date is required!',
          });
          return null;
        }

        if (!stringToDate.safeParse(date).success) {
          ctx.addIssue({
            code: zod.ZodIssueCode.invalid_date,
            message: props?.message?.invalid_type_error ?? 'Invalid Date!!',
          });
        }

        return date;
      })
      .pipe(zod.union([zod.number(), zod.string(), zod.date(), zod.null()])),
  /**
   * Editor
   * defaultValue === '' | <p></p>
   */
  editor: (props?: InputProps) =>
    zod.string().min(8, { message: props?.message?.required_error ?? 'Editor is required!' }),
  /**
   * Object
   * defaultValue === null
   */
  objectOrNull: <T>(props?: InputProps) =>
    zod.custom<T | null>().refine((data) => data !== null && data !== '', {
      message: props?.message?.required_error ?? 'Field is required!',
    }),
  /**
   * Boolean
   * defaultValue === false
   */
  // boolean: (props?: InputProps) =>
  //   zod.coerce.boolean().refine((bool) => bool === true, {
  //     message: props?.message?.required_error ?? 'Switch is required!',
  //   }),
  boolean: (props?: InputProps) =>
    zod.coerce.boolean({
      required_error: props?.message?.required_error ?? 'This field is required!',
      invalid_type_error: props?.message?.invalid_type_error ?? 'Invalid value!',
    }),

  /**
   * File
   * defaultValue === '' || null
   */
  file: (props?: InputProps) =>
    zod.custom<File | string | null>().transform((data, ctx) => {
      const hasFile = data instanceof File || (typeof data === 'string' && !!data.length);

      if (!hasFile) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: props?.message?.required_error ?? 'File is required!',
        });
        return null;
      }

      return data;
    }),
  /**
   * Files
   * defaultValue === []
   */
  files: (props?: InputProps) =>
    zod.array(zod.custom<File | string>()).transform((data, ctx) => {
      const minFiles = props?.minFiles ?? 2;

      if (!data.length) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: props?.message?.required_error ?? 'Files is required!',
        });
      } else if (data.length < minFiles) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: `Must have at least ${minFiles} items!`,
        });
      }

      return data;
    }),
  /**
   * Number
   * defaultValue === null
   */
  number: (props?: InputProps) =>
    zod
      .preprocess((val) => {
        if (typeof val === 'string') {
          const cleaned = val.trim();
          if (cleaned === '') return undefined;
          const parsed = parseFloat(cleaned);
          return Number.isFinite(parsed) ? parsed : undefined;
        }
        if (typeof val === 'number') {
          return Number.isFinite(val) ? val : undefined;
        }
        return undefined;
      }, zod.number().optional())
      .superRefine((val, ctx) => {
        if (props?.required && (val === undefined || val === null)) {
          ctx.addIssue({
            code: zod.ZodIssueCode.custom,
            message: props?.message?.required_error ?? 'This field is required!',
          });
        }
      }),

  /**
   * Float Number
   * defaultValue === null
   */
  floatNumber: (props?: InputProps) =>
    zod.preprocess(
      (value) => {
        if (typeof value === 'string') {
          const parsed = parseFloat(value);
          return Number.isNaN(parsed) ? undefined : parsed; // Coerce string to number or return undefined
        }
        return value;
      },
      zod
        .number({
          required_error: props?.message?.required_error ?? 'This field is required!',
          invalid_type_error: props?.message?.invalid_type_error ?? 'Invalid float number!',
        })
        .refine((num) => num % 1 === 0 || num % 1 !== 0, {
          message: props?.message?.invalid_type_error ?? 'Value must be a valid float number!',
        }) // Allow both integers and floats, including floats like 4.0
    ),

  /**
   * Array with at least one element
   * defaultValue === []
   */
  array: <T>(props?: InputProps) =>
    zod
      .array(zod.custom<T>())
      .min(1, { message: props?.message?.required_error ?? 'At least one item is required!' }),
};
